import { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  type AWSNodeConfigApiParameters,
  type CloudFlowNodeType,
  CloudFlowProvider,
  type GCPNodeConfigApiParameters,
} from "@doitintl/cmp-models";

import { useCloudflowState } from "../Common/hooks/useCloudflowState";
import { GCPHierarchicalResource, PERMISSIONS_PROJECT_ID_KEY } from "../utils/gcpUtils";
import { useCloudPermissions } from "./hooks";
import { useNodeConfigurationContext } from "./NodeConfigurationContext";

interface AWSPermissionConfig {
  accountId: string | undefined;
}
interface GCPPermissionConfig {
  serviceAccountResource: string | undefined;
  [PERMISSIONS_PROJECT_ID_KEY]: string | undefined;
}

function useCheckAWSPermissions(
  awsPermissionConfig: AWSPermissionConfig,
  setConfig: (configs: AWSPermissionConfig) => void
) {
  const { isPublished, isBlueprint } = useCloudflowState();
  const { nodeConfig } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const accountId = (nodeConfig.parameters as AWSNodeConfigApiParameters).configurationValues?.accountId;

  const { updatePermissions, loading } = useCloudPermissions({
    permissionConfigValues: { accountId },
    autoUpdate: false,
  });

  useEffect(() => {
    if (nodeConfig.parameters.provider !== CloudFlowProvider.AWS) return;
    if (!accountId || loading || isPublished || isBlueprint) return;
    if (accountId && accountId !== awsPermissionConfig.accountId) {
      updatePermissions();
      setConfig({ accountId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    loading,
    isPublished,
    isBlueprint,
    awsPermissionConfig.accountId,
    nodeConfig.parameters.provider,
    setConfig,
  ]);
}

function useCheckGCPPermissions(
  gcpPermissionConfig: GCPPermissionConfig,
  setConfig: (configs: GCPPermissionConfig) => void
) {
  const { isPublished, isBlueprint } = useCloudflowState();
  const { nodeConfig } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const { serviceAccountResource } = (nodeConfig.parameters as GCPNodeConfigApiParameters).configurationValues;

  // Extract projectId from serviceAccountResource if it's a project
  const projectId = serviceAccountResource?.startsWith("projects/") ? serviceAccountResource.split("/")[1] : undefined;

  const { updatePermissions, loading } = useCloudPermissions({
    permissionConfigValues: {
      serviceAccountResource,
      hierarchicalResource: serviceAccountResource?.startsWith("organizations/")
        ? GCPHierarchicalResource.ORGANIZATION
        : GCPHierarchicalResource.PROJECT,
      [PERMISSIONS_PROJECT_ID_KEY]: projectId,
    },
    autoUpdate: false,
  });

  const hasConfigChanged = gcpPermissionConfig.serviceAccountResource !== serviceAccountResource;

  useEffect(() => {
    if (nodeConfig.parameters.provider !== CloudFlowProvider.GCP) return;
    if (!serviceAccountResource || loading || isPublished || isBlueprint) return;
    if (serviceAccountResource && hasConfigChanged) {
      updatePermissions();
      setConfig({
        serviceAccountResource,
        [PERMISSIONS_PROJECT_ID_KEY]: projectId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serviceAccountResource,
    loading,
    isPublished,
    isBlueprint,
    gcpPermissionConfig.serviceAccountResource,
    nodeConfig.parameters.provider,
    setConfig,
    hasConfigChanged,
  ]);
}

interface PermissionStateContextType {
  awsPermissionConfig: AWSPermissionConfig;
  gcpPermissionConfig: GCPPermissionConfig;
  setAWSPermissionConfig: (config: AWSPermissionConfig) => void;
  setGCPPermissionConfig: (config: GCPPermissionConfig) => void;
}

const PermissionStateContext = createContext<PermissionStateContextType>({
  awsPermissionConfig: { accountId: undefined },
  gcpPermissionConfig: {
    serviceAccountResource: undefined,
    [PERMISSIONS_PROJECT_ID_KEY]: undefined,
  },
  setAWSPermissionConfig: () => {},
  setGCPPermissionConfig: () => {},
});

export function PermissionStateProvider({ children }: { children: React.ReactNode }) {
  const [awsPermissionConfig, setAWSPermissionConfig] = useState<AWSPermissionConfig>({ accountId: undefined });
  const [gcpPermissionConfig, setGCPPermissionConfig] = useState<GCPPermissionConfig>({
    serviceAccountResource: undefined,
    [PERMISSIONS_PROJECT_ID_KEY]: undefined,
  });

  useCheckAWSPermissions(awsPermissionConfig, setAWSPermissionConfig);
  useCheckGCPPermissions(gcpPermissionConfig, setGCPPermissionConfig);

  const value = useMemo(
    () => ({
      awsPermissionConfig,
      gcpPermissionConfig,
      setAWSPermissionConfig,
      setGCPPermissionConfig,
    }),
    [awsPermissionConfig, gcpPermissionConfig]
  );

  return <PermissionStateContext.Provider value={value}>{children}</PermissionStateContext.Provider>;
}

export function usePermissionState() {
  const context = useContext(PermissionStateContext);
  if (!context) {
    throw new Error("usePermissionState must be used within a PermissionStateProvider");
  }
  return context;
}
